import { c as _c } from "react/compiler-runtime";
import { Operator } from '@netpurpose/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { useMemo } from 'react';
import { GeneratedUsersApi } from '../../GeneratedApi';
import type { IngestionTaskStep } from '../../generated/facts';
import { transformUserWithPermissions, type UserInfo } from '../../models';
import type { ReverseFieldMap } from '../../queryBuilder';
import { snakeToCamelKeys } from '../../utils';
import { getPaginationConfig, useUrlApiConnector } from '../useUrlApiConnector';
const tableToApiFieldMap: Partial<ReverseFieldMap<keyof UserInfo>> = {
  combinedName: 'combined_name',
  email: 'email',
  groups: 'groups.name',
  lastLogin: 'last_login',
  userPersona: 'user_persona',
  userId: 'user_id',
  active: 'active'
};
export const PAGINATED_USERS_QUERY_CACHE_KEY = 'paginatedUsers';
export const USERS_BY_ID_QUERY_CACHE_KEY = 'usersById';
export const usePaginatedUsers = () => {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      tableToApiFieldMap,
      urlKey: PAGINATED_USERS_QUERY_CACHE_KEY,
      perPage: 10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    queryString,
    filterConfig,
    initialPaginationConfig
  } = useUrlApiConnector(t0);
  let t1;
  let t2;
  if ($[1] !== queryString) {
    t1 = [PAGINATED_USERS_QUERY_CACHE_KEY, queryString];
    t2 = () => GeneratedUsersApi.users.getUsers({
      query: {
        q: queryString
      }
    });
    $[1] = queryString;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = !!queryString;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      placeholderData: keepPreviousData
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = useQuery(t4);
  let data;
  let rest;
  if ($[8] !== t5) {
    ({
      data,
      ...rest
    } = t5);
    $[8] = t5;
    $[9] = data;
    $[10] = rest;
  } else {
    data = $[9];
    rest = $[10];
  }
  let t6;
  if ($[11] !== data) {
    t6 = data?.results ? data.results.map(transformUserWithPermissions) : undefined;
    $[11] = data;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const transformedUsers = t6;
  const t7 = data?.total;
  let t8;
  if ($[13] !== initialPaginationConfig || $[14] !== t7) {
    t8 = getPaginationConfig({
      numResults: t7,
      paginationConfig: initialPaginationConfig
    });
    $[13] = initialPaginationConfig;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const paginationConfig = t8;
  let t9;
  if ($[16] !== transformedUsers) {
    t9 = {
      users: transformedUsers
    };
    $[16] = transformedUsers;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== filterConfig || $[19] !== paginationConfig || $[20] !== rest || $[21] !== t9) {
    t10 = {
      ...rest,
      data: t9,
      filterConfig,
      paginationConfig
    };
    $[18] = filterConfig;
    $[19] = paginationConfig;
    $[20] = rest;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
export const useReassignableUsers = t0 => {
  const $ = _c(16);
  const {
    step,
    theme,
    enabled
  } = t0;
  let t1;
  let t2;
  if ($[0] !== step || $[1] !== theme) {
    t1 = [step, theme];
    t2 = () => GeneratedUsersApi.users.getAssignableUsers({
      path: {
        step,
        theme
      }
    });
    $[0] = step;
    $[1] = theme;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = useQuery(t3);
  let data;
  let rest;
  if ($[8] !== t4) {
    ({
      data,
      ...rest
    } = t4);
    $[8] = t4;
    $[9] = data;
    $[10] = rest;
  } else {
    data = $[9];
    rest = $[10];
  }
  let t5;
  if ($[11] !== data) {
    t5 = data?.map(snakeToCamelKeys);
    $[11] = data;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== rest || $[14] !== t5) {
    t6 = {
      data: t5,
      ...rest
    };
    $[13] = rest;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export const useUsersByIds = t0 => {
  const $ = _c(20);
  const {
    userIds
  } = t0;
  let t1;
  if ($[0] !== userIds) {
    const uniqueUserIds = userIds && Array.from(new Set(userIds));
    const overlapsUserIdKey = `${Operator.Overlaps}user_id`;
    const combinedParams = {
      limit: 9999,
      page: 0,
      [overlapsUserIdKey]: uniqueUserIds
    };
    t1 = qs.stringify(combinedParams, {
      skipNulls: true,
      arrayFormat: "repeat"
    });
    $[0] = userIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryString = t1;
  let t2;
  let t3;
  if ($[2] !== queryString) {
    t2 = [USERS_BY_ID_QUERY_CACHE_KEY, queryString];
    t3 = () => GeneratedUsersApi.users.getUsers({
      query: {
        q: queryString
      }
    });
    $[2] = queryString;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = !!userIds?.length;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = useQuery(t5);
  let data;
  let rest;
  if ($[9] !== t6) {
    ({
      data,
      ...rest
    } = t6);
    $[9] = t6;
    $[10] = data;
    $[11] = rest;
  } else {
    data = $[10];
    rest = $[11];
  }
  let t7;
  if ($[12] !== data) {
    t7 = data?.results ? data.results.map(transformUserWithPermissions) : undefined;
    $[12] = data;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const transformedUsers = t7;
  const t8 = data?.total;
  let t9;
  if ($[14] !== t8 || $[15] !== transformedUsers) {
    t9 = {
      users: transformedUsers,
      numResults: t8
    };
    $[14] = t8;
    $[15] = transformedUsers;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== rest || $[18] !== t9) {
    t10 = {
      ...rest,
      data: t9
    };
    $[17] = rest;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
export type ReturnedUser = NonNullable<ReturnType<typeof useUsersByIds>['data']['users']>[number];